import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import Button from '../common/Button';
import Input from '../common/Input';
import { USER_ROLE_LABELS, USER_ROLES } from '../../utils/constants';
import { useAuth } from '../../context/AuthContext';
import { eventAPI, organizationRolesAPI, userAPI } from '../../api/endpoints';

const AddUserModal = ({ isOpen, onClose, onSubmit, loading, initialRole = 'usher' }: any) => {
  const { permissions, user: currentUser } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    role: initialRole,
    event_id: '',
    organization_name: '',
    sms_sender_id: '',
    sms_balance: 0,
    organization_role_id: '',
    global_event_access: false,
    tenant_id: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Get allowed roles for current user
  const allowedRoles = permissions.getAllowedRolesToCreate();

  // Fetch events for admins to assign users
  const { data: eventsData } = useQuery({
    queryKey: ['events'],
    queryFn: async () => {
      const response = await eventAPI.getAll({});
      return response.data;
    },
    enabled: (permissions.isEventAdmin() || permissions.isSuperAdmin()) && isOpen,
  });

  // Fetch custom roles
  const { data: rolesData } = useQuery({
    queryKey: ['organizationRoles'],
    queryFn: async () => {
      const response = await organizationRolesAPI.getAll();
      return response.data;
    },
    enabled: permissions.isEventAdmin() && isOpen,
  });

  // Fetch organizations for super admin
  const { data: organizationsData } = useQuery({
    queryKey: ['organizations'],
    queryFn: async () => {
      const response = await userAPI.getOrganizations();
      return response.data;
    },
    enabled: permissions.isSuperAdmin() && isOpen,
  });

  const events = eventsData?.events || [];
  const customRoles = rolesData?.data || [];
  const organizations = organizationsData?.organizations || [];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData(prev => {
      const newValue = type === 'checkbox' ? checked : value;
      
      // If they change the role dropdown, handle organization_role_id vs system role
      if (name === 'organization_role_id') {
        if (!value) {
           return { ...prev, organization_role_id: '', role: 'usher' }; // default fallback
        }
        
        // Find the selected role
        const selectedRole = customRoles.find(r => r.id === value);
        if (selectedRole) {
          // If system role (usher, front_desk), we map it directly to the base role
          const baseRole = selectedRole.is_system ? selectedRole.slug : 'usher';
          return { ...prev, organization_role_id: value, role: baseRole };
        }
      }

      // Auto-select organization when event is selected (for Super Admins)
      if (name === 'event_id' && newValue && permissions.isSuperAdmin()) {
        const selectedEvent = events.find(e => String(e.id) === String(newValue));
        if (selectedEvent && selectedEvent.created_by) {
          return { ...prev, [name]: newValue, tenant_id: selectedEvent.created_by };
        }
      }
      
      return { ...prev, [name]: newValue };
    });
    
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.email) newErrors.email = 'Email is required';
    if (!formData.email.includes('@')) newErrors.email = 'Invalid email';
    if (!formData.full_name) newErrors.full_name = 'Full name is required';
    
    // Event admin must select an event OR enable global access
    if (permissions.isEventAdmin() && !formData.global_event_access && !formData.event_id) {
      newErrors.event_id = 'Please select an event or enable Global Access';
    }
    
    if (formData.sms_sender_id && formData.sms_sender_id.length > 11) {
      newErrors.sms_sender_id = 'Sender ID max 11 characters';
    }
    
    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    console.log('🚀 AddUserModal - Submitting user data:', formData);
    console.log('🚀 AddUserModal - Allowed roles:', allowedRoles);
    console.log('🚀 AddUserModal - Is event admin:', permissions.isEventAdmin());
    
    const payload = {
      ...formData,
      sms_balance: formData.sms_balance !== undefined && (formData.sms_balance as any) !== '' 
        ? parseInt(formData.sms_balance as any, 10) 
        : 0,
      sms_sender_id: formData.sms_sender_id ? formData.sms_sender_id.trim() : null,
    };
    onSubmit(payload);
  };

  const handleClose = () => {
    setFormData({ email: '', full_name: '', role: initialRole, event_id: '', organization_name: '', sms_sender_id: '', sms_balance: 0, organization_role_id: '', global_event_access: false, tenant_id: '' });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={handleClose}
        />

        {/* Modal */}
        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md transform transition-all">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b">
            <h3 className="text-xl font-semibold text-gray-900">Add New User</h3>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              disabled={loading}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <Input
              label="Full Name"
              name="full_name"
              value={formData.full_name}
              onChange={handleChange}
              error={errors.full_name}
              placeholder="John Doe"
              required
            />

            <Input
              label="Email Address"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              error={errors.email}
              placeholder="john@example.com"
              required
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Role
              </label>
              <select
                name="role"
                value={formData.role}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {allowedRoles.map(role => (
                  <option key={role} value={role}>
                    {USER_ROLE_LABELS[role]}
                  </option>
                ))}
              </select>
              {customRoles.length > 0 && permissions.isEventAdmin() && (
                <div className="mt-2">
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Or assign custom role (optional)
                  </label>
                  <select
                    name="organization_role_id"
                    value={formData.organization_role_id}
                    onChange={handleChange}
                    className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">Default role permissions</option>
                    {customRoles.map(role => (
                      <option key={role.id} value={role.id}>
                        {role.name} {role.is_system ? '(System)' : '(Custom)'}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {errors.role && (
                <p className="mt-1 text-sm text-red-600">{errors.role}</p>
              )}
              {errors.organization_role_id && (
                <p className="mt-1 text-sm text-red-600">{errors.organization_role_id}</p>
              )}
            </div>

            {/* Organization details for Event Admins */}
            {formData.role === 'event_admin' && (
              <>
                <Input
                  label="Organization Name"
                  name="organization_name"
                  value={formData.organization_name}
                  onChange={handleChange}
                  error={errors.organization_name}
                  placeholder="e.g. Acme Corp"
                />

                {currentUser?.role === USER_ROLE_LABELS.super_admin || currentUser?.role === 'super_admin' ? (
                  <Input
                    label="SMS Sender ID (Super Admin Only)"
                    name="sms_sender_id"
                    value={formData.sms_sender_id}
                    onChange={handleChange}
                    error={errors.sms_sender_id}
                    placeholder="e.g. InfoSMS"
                    maxLength={11}
                    helperText="Leave empty to use system default"
                  />
                ) : null}
              </>
            )}

            {/* Event Selection for Admins */}
            {(permissions.isEventAdmin() || permissions.isSuperAdmin()) && (
              <div className="space-y-4 pt-2 border-t border-gray-100">
                <h3 className="text-sm font-medium text-gray-900">Organization & Event Access</h3>
                
                {permissions.isSuperAdmin() && formData.role !== 'super_admin' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Assign to Organization (Optional)
                    </label>
                    <select
                      name="tenant_id"
                      value={formData.tenant_id}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="">New Main Organization (Independent)</option>
                      {organizations.map(org => (
                        <option key={org.id} value={org.id}>
                          {org.organization_name || org.full_name} ({org.email})
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-sm text-gray-500">
                      Select an existing organization to assign as a Secondary Administrator, or leave empty to create a new Main Organization Administrator.
                    </p>
                  </div>
                )}

                {permissions.isEventAdmin() && (
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      name="global_event_access"
                      checked={formData.global_event_access}
                      onChange={handleChange}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700">Grant access to ALL organization events</span>
                  </label>
                )}

                {!formData.global_event_access && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Assign to Specific Event {permissions.isSuperAdmin() ? '(Optional)' : ''}
                    </label>
                    <select
                      name="event_id"
                      value={formData.event_id}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="">Select an event...</option>
                      {events.map(event => (
                        <option key={event.id} value={event.id}>
                          {event.name}
                        </option>
                      ))}
                    </select>
                    {errors.event_id && (
                      <p className="mt-1 text-sm text-red-600">{errors.event_id}</p>
                    )}
                    <p className="mt-1 text-sm text-gray-500">
                      User will be assigned to the selected event
                    </p>
                  </div>
                )}
              </div>
            )}

            {currentUser?.role === USER_ROLES.SUPER_ADMIN && formData.role === 'event_admin' && (
              <div className="pt-2 border-t border-gray-100">
                <h3 className="text-sm font-medium text-gray-900 mb-3">SMS Configuration (Super Admin Only)</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="SMS Sender ID"
                    name="sms_sender_id"
                    value={formData.sms_sender_id}
                    onChange={handleChange}
                    error={errors.sms_sender_id}
                    placeholder="e.g. MyOrg (Max 11 chars)"
                  />
                  <Input
                    label="SMS Balance"
                    name="sms_balance"
                    type="number"
                    value={formData.sms_balance}
                    onChange={handleChange}
                    error={errors.sms_balance}
                    placeholder="0"
                    min="0"
                  />
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="secondary"
                onClick={handleClose}
                className="flex-1"
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1"
                loading={loading}
              >
                Add User
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddUserModal;
